import Auth from '/imports/ui/services/auth';
import logger from '/imports/startup/client/logger';
import BridgeService from './service';
import ScreenshareBroker from '/imports/ui/services/bbb-webrtc-sfu/screenshare-broker';
import { setIsSharing, screenShareEndAlert, setOutputDeviceId } from '/imports/ui/components/screenshare/service';
import { SCREENSHARING_ERRORS } from './errors';
import { shouldForceRelay } from '/imports/ui/services/bbb-webrtc-sfu/utils';
import MediaStreamUtils from '/imports/utils/media-stream-utils';
import { notifyStreamStateChange } from '/imports/ui/services/bbb-webrtc-sfu/stream-state-service';
import ScreenshareQualityMonitor from '/imports/ui/services/screenshare/quality-monitor';

const BRIDGE_NAME = 'kurento'
const SCREENSHARE_VIDEO_TAG = 'screenshareVideo';
const SEND_ROLE = 'send';
const RECV_ROLE = 'recv';
const DEFAULT_VOLUME = 1;
const RECONNECT_COOLDOWN_MS = 2000;
const MAX_RECONNECT_CYCLES = 3;

// the error-code mapping is bridge specific; that's why it's not in the errors util
const ERROR_MAP = {
  1301: SCREENSHARING_ERRORS.SIGNALLING_TRANSPORT_DISCONNECTED,
  1302: SCREENSHARING_ERRORS.SIGNALLING_TRANSPORT_CONNECTION_FAILED,
  1305: SCREENSHARING_ERRORS.PEER_NEGOTIATION_FAILED,
  1307: SCREENSHARING_ERRORS.ICE_STATE_FAILED,
  1310: SCREENSHARING_ERRORS.ENDED_WHILE_STARTING,
};

const mapErrorCode = (error) => {
  const { errorCode } = error;
  const mappedError = ERROR_MAP[errorCode];

  if (errorCode == null || mappedError == null) return error;
  error.errorCode = mappedError.errorCode;
  error.errorMessage = mappedError.errorMessage;
  error.message = mappedError.errorMessage;

  return error;
}

export default class KurentoScreenshareBridge {
  constructor() {
    this.role;
    this.broker;
    this._gdmStream;
    this.hasAudio = false;
    this.connectionAttempts = 0;
    this.reconnecting = false;
    this.reconnectionTimeout;
    this._restartIntervalMs = null;
    this.startedOnce = false;
    this.outputDeviceId = null;
    this.bridgeName = BRIDGE_NAME;
    this._reconnectCycleCount = 0;
    this._forceRelay = false;
    this._qualityMonitor = null;
  }

  get restartIntervalMs() {
    return this._restartIntervalMs || BridgeService.BASE_MEDIA_TIMEOUT();
  }

  set restartIntervalMs(value) {
    this._restartIntervalMs = value;
  }

  get gdmStream() {
    return this._gdmStream;
  }

  set gdmStream(stream) {
    this._gdmStream = stream;
  }

  _shouldReconnect() {
    return this.reconnectionTimeout == null
      && (this.role === RECV_ROLE || this.role === SEND_ROLE);
  }

  /**
   * Get the RTCPeerConnection object related to the screensharing stream.
   * @returns {Object} The RTCPeerConnection object related to the presenter/
   *                   viewer peer. If there's no stream being shared, returns
   *                   null.
   */
  getPeerConnection() {
    try {
      let peerConnection = null;

      if (this.broker && this.broker.webRtcPeer) {
        peerConnection = this.broker.webRtcPeer.peerConnection;
      }

      return peerConnection;
    } catch (error) {
      return null;
    }
  }

  setStreamEnabled(enabled) {
    if (this.gdmStream) {
      this.gdmStream.getTracks().forEach((track) => {
        // eslint-disable-next-line no-param-reassign
        track.enabled = enabled;
      });
    }
  }

  inboundStreamReconnect() {
    this._reconnectCycleCount += 1;

    if (this._reconnectCycleCount > MAX_RECONNECT_CYCLES) {
      logger.error({
        logCode: 'screenshare_viewer_max_reconnect',
        extraInfo: { cycles: this._reconnectCycleCount, role: this.role },
      }, 'Screenshare viewer hit max reconnect cycles — giving up');
      this._reconnectCycleCount = 0;
      this.reconnecting = false;
      this._stop();
      notifyStreamStateChange('screenshare', 'failed');
      return;
    }

    const currentRestartIntervalMs = this.restartIntervalMs;

    logger.warn({
      logCode: 'screenshare_viewer_reconnect',
      extraInfo: {
        reconnecting: this.reconnecting,
        role: this.role,
        bridge: BRIDGE_NAME,
        cycle: this._reconnectCycleCount,
      },
    }, `Screenshare viewer reconnecting (cycle ${this._reconnectCycleCount}/${MAX_RECONNECT_CYCLES})`);

    // Cleanly stop everything before triggering a reconnect
    this._stop();
    // Create new reconnect interval time
    this.restartIntervalMs = BridgeService.getNextReconnectionInterval(currentRestartIntervalMs);

    // Cooldown before reconnecting to let the SFU clean up
    setTimeout(() => {
      this.view(this.streamId, { hasAudio: this.hasAudio, outputDeviceId: this.outputDeviceId }).then(() => {
        this.clearReconnectionTimeout();
      }).catch((error) => {
        // Error handling is a no-op because it will be "handled" in handleViewerFailure
        logger.debug({
          logCode: 'screenshare_reconnect_failed',
          extraInfo: {
            errorCode: error.errorCode,
            errorMessage: error.errorMessage,
            reconnecting: this.reconnecting,
            role: this.role,
            bridge: BRIDGE_NAME
          },
        }, 'Screensharing reconnect failed');
      });
    }, RECONNECT_COOLDOWN_MS);
  }

  outboundStreamReconnect() {
    this._reconnectCycleCount += 1;

    if (this._reconnectCycleCount > MAX_RECONNECT_CYCLES) {
      logger.error({
        logCode: 'screenshare_presenter_max_reconnect',
        extraInfo: { cycles: this._reconnectCycleCount, role: this.role },
      }, 'Screenshare presenter hit max reconnect cycles — giving up');
      this._reconnectCycleCount = 0;
      this.reconnecting = false;
      this._stop();
      notifyStreamStateChange('screenshare', 'failed');
      if (this.onerror) this.onerror(SCREENSHARING_ERRORS.MEDIA_TIMEOUT);
      return;
    }

    const currentRestartIntervalMs = this.restartIntervalMs;

    logger.warn({
      logCode: 'screenshare_presenter_reconnect',
      extraInfo: {
        reconnecting: this.reconnecting,
        role: this.role,
        bridge: BRIDGE_NAME,
        hasGdmStream: !!this.gdmStream,
        gdmStreamActive: this.gdmStream?.active,
        cycle: this._reconnectCycleCount,
      },
    }, `Screenshare presenter reconnecting (cycle ${this._reconnectCycleCount}/${MAX_RECONNECT_CYCLES})`);

    // Check if the local screen capture stream is still alive
    if (!this.gdmStream || !this.gdmStream.active) {
      logger.error({
        logCode: 'screenshare_presenter_reconnect_no_stream',
        extraInfo: {
          hasGdmStream: !!this.gdmStream,
          gdmStreamActive: this.gdmStream?.active,
        },
      }, 'Presenter reconnect failed: gdmStream not active, user must re-share');
      this.reconnecting = false;
      if (this.onerror) this.onerror(SCREENSHARING_ERRORS.ENDED_WHILE_STARTING);
      return;
    }

    const allTracksAlive = this.gdmStream.getTracks().every(
      (track) => track.readyState !== 'ended'
    );
    if (!allTracksAlive) {
      logger.error({
        logCode: 'screenshare_presenter_reconnect_tracks_ended',
        extraInfo: {
          trackStates: this.gdmStream.getTracks().map(
            (t) => ({ kind: t.kind, state: t.readyState })
          ),
        },
      }, 'Presenter reconnect failed: tracks ended, user must re-share');
      this.reconnecting = false;
      if (this.onerror) this.onerror(SCREENSHARING_ERRORS.ENDED_WHILE_STARTING);
      return;
    }

    // Clean up broker without destroying gdmStream (_stop preserves it)
    this._stop();
    this.restartIntervalMs = BridgeService.getNextReconnectionInterval(currentRestartIntervalMs);

    // Cooldown before reconnecting to let the SFU clean up
    setTimeout(() => {
      // [Fix #11] Was a dead ternary — both branches returned 'screenshare'
      const contentType = 'screenshare';
      this.share(this.gdmStream, (error) => {
        logger.error({
          logCode: 'screenshare_presenter_reconnect_share_failed',
          extraInfo: {
            errorCode: error?.errorCode,
            errorMessage: error?.errorMessage,
          },
        }, `Presenter reconnect share failed: ${error?.errorMessage}`);
      }, contentType).then(() => {
        this.clearReconnectionTimeout();
        logger.info({
          logCode: 'screenshare_presenter_reconnect_success',
        }, 'Screenshare presenter reconnected successfully');
      }).catch((error) => {
        logger.error({
          logCode: 'screenshare_presenter_reconnect_error',
          extraInfo: {
            errorCode: error?.errorCode,
            errorMessage: error?.errorMessage,
          },
        }, `Screenshare presenter reconnect error: ${error?.errorMessage}`);
      });
    }, RECONNECT_COOLDOWN_MS);
  }

  handleConnectionTimeoutExpiry() {
    this.reconnecting = true;

    switch (this.role) {
      case RECV_ROLE:
        return this.inboundStreamReconnect();

      case SEND_ROLE:
        return this.outboundStreamReconnect();

      default:
        this.reconnecting = false;
        logger.error({
          logCode: 'screenshare_wont_reconnect',
          extraInfo: {
            role: this.broker?.role || this.role,
            started: !!(this.broker?.started),
            bridge: BRIDGE_NAME,
          },
        }, 'Screen sharing will not reconnect - unknown role');
        break;
    }
  }

  maxConnectionAttemptsReached () {
    return this.connectionAttempts > BridgeService.MAX_CONN_ATTEMPTS();
  }

  scheduleReconnect({
    overrideTimeout,
  } = { }) {
    if (this.reconnectionTimeout == null) {
      let nextRestartInterval = this.restartIntervalMs;
      if (typeof overrideTimeout === 'number') nextRestartInterval = overrideTimeout;

      this.reconnectionTimeout = setTimeout(
        this.handleConnectionTimeoutExpiry.bind(this),
        nextRestartInterval,
      );
    }
  }

  clearReconnectionTimeout () {
    this.reconnecting = false;
    this.restartIntervalMs = BridgeService.BASE_MEDIA_TIMEOUT();

    if (this.reconnectionTimeout) {
      clearTimeout(this.reconnectionTimeout);
      this.reconnectionTimeout = null;
    }
  }

  setVolume(volume) {
    const mediaElement = document.getElementById(SCREENSHARE_VIDEO_TAG);

    if (mediaElement) {
      if (typeof volume === 'number' && volume >= 0 && volume <= 1) {
        mediaElement.volume = volume;
      }

      return mediaElement.volume;
    }

    return DEFAULT_VOLUME;
  }

  getVolume() {
    const mediaElement = document.getElementById(SCREENSHARE_VIDEO_TAG);

    if (mediaElement) return mediaElement.volume;

    return DEFAULT_VOLUME;
  }

  handleViewerStart() {
    const mediaElement = document.getElementById(SCREENSHARE_VIDEO_TAG);

    logger.info({
      logCode: 'screenshare_viewer_start_handler',
      extraInfo: {
        hasMediaElement: !!mediaElement,
        hasBroker: !!this.broker,
        hasWebRtcPeer: !!(this.broker?.webRtcPeer),
        hasAudio: this.hasAudio,
        outputDeviceId: this.outputDeviceId,
      },
    }, `[SCREENSHARE VIEWER] handleViewerStart called - hasMediaElement=${!!mediaElement}, hasBroker=${!!this.broker}`);

    if (mediaElement && this.broker && this.broker.webRtcPeer) {
      const stream = this.broker.webRtcPeer.getRemoteStream();

      logger.info({
        logCode: 'screenshare_viewer_got_stream',
        extraInfo: {
          hasStream: !!stream,
          streamActive: stream?.active,
          trackCount: stream?.getTracks()?.length || 0,
        },
      }, `[SCREENSHARE VIEWER] Got remote stream - active=${stream?.active}, tracks=${stream?.getTracks()?.length || 0}`);

      if (this.hasAudio && this.outputDeviceId && typeof this.outputDeviceId === 'string') {
        setOutputDeviceId(this.outputDeviceId);
      }

      try {
        BridgeService.screenshareLoadAndPlayMediaStream(stream, mediaElement, !this.broker.hasAudio);
        logger.info({
          logCode: 'screenshare_viewer_stream_loaded',
        }, '[SCREENSHARE VIEWER] Stream loaded into media element');
      } catch (error) {
        logger.error({
          logCode: 'screenshare_viewer_stream_load_failed',
          extraInfo: {
            errorName: error.name,
            errorMessage: error.message,
          },
        }, `[SCREENSHARE VIEWER] Failed to load/play stream: ${error.message}`);
        notifyStreamStateChange('screenshare', 'failed');
        return;
      }
    } else {
      logger.warn({
        logCode: 'screenshare_viewer_start_missing_deps',
        extraInfo: {
          hasMediaElement: !!mediaElement,
          hasBroker: !!this.broker,
          hasWebRtcPeer: !!(this.broker?.webRtcPeer),
        },
      }, '[SCREENSHARE VIEWER] handleViewerStart missing dependencies');
    }

    this.startedOnce = true;
    this.clearReconnectionTimeout();
    this.connectionAttempts = 0;
    this._reconnectCycleCount = 0;
    this._forceRelay = false;

    // Start quality monitor for viewer screenshare
    this._startQualityMonitor();
  }

  _startQualityMonitor() {
    this._stopQualityMonitor();
    const pc = this.getPeerConnection();
    if (pc) {
      this._qualityMonitor = new ScreenshareQualityMonitor(pc, () => {
        logger.warn({
          logCode: 'screenshare_quality_monitor_kill',
          extraInfo: { role: this.role },
        }, 'Quality monitor triggered screenshare stop due to packet loss');
        this.stop();
      });
      this._qualityMonitor.start();
    }
  }

  _stopQualityMonitor() {
    if (this._qualityMonitor) {
      this._qualityMonitor.stop();
      this._qualityMonitor = null;
    }
  }

  handleBrokerFailure(error) {
    mapErrorCode(error);
    const { errorMessage, errorCode } = error;

    logger.error({
      logCode: 'screenshare_broker_failure',
      extraInfo: {
        errorCode,
        errorMessage,
        role: this.broker?.role,
        started: this.broker?.started,
        reconnecting: this.reconnecting,
        bridge: BRIDGE_NAME,
      },
    }, `[SCREENSHARE VIEWER] Broker failure - errorCode=${errorCode}, errorMessage=${errorMessage}`);

    notifyStreamStateChange('screenshare', 'failed');

    // SFU_INVALID_REQUEST (2300) = SFU rejected the request outright
    //   recv: no screenshare exists to subscribe to
    //   send: SFU won't accept the stream (e.g., stale session)
    // SFU_UNAUTHORIZED (2301) = SFU auth check failed
    //   send/recv: session/user not authorized for this operation
    // Neither is transient — cancel any scheduled reconnect and stop.
    // Prevents the phantom reconnect loop that poisons the SFU.
    if ((errorCode === 2300 || errorCode === 2301) && (this.role === RECV_ROLE || this.role === SEND_ROLE)) {
      this.clearReconnectionTimeout();
      logger.info({
        logCode: 'screenshare_sfu_rejected_stop',
        extraInfo: { errorCode, role: this.role },
      }, `SFU rejected screenshare request (code=${errorCode}, role=${this.role}) — not retrying`);
      return error;
    }

    // ICE_STATE_FAILED: if retryThroughRelay is enabled, force TURN relay
    // on the next attempt. Mirrors the pattern in sfu-audio-bridge.js.
    // After mapErrorCode, errorCode is the mapped code (1107) or the original
    // broker code (1307) if unmapped. Check both.
    const iceFailedCode = SCREENSHARING_ERRORS.ICE_STATE_FAILED.errorCode;
    // errorCode may be string or number depending on mapping path; use == for coercion
    if (errorCode == iceFailedCode || errorCode == 1307) {
      const SETTINGS = window.meetingClientSettings;
      const RETRY_THROUGH_RELAY = SETTINGS.public.kurento?.screenshare?.retryThroughRelay || false;
      if (RETRY_THROUGH_RELAY && !this._forceRelay) {
        this._forceRelay = true;
        logger.warn({
          logCode: 'screenshare_force_relay_on_retry',
          extraInfo: { errorCode, role: this.role },
        }, 'ICE failed for screenshare — will force TURN relay on next attempt');
      }
    }

    // Screensharing was already successfully negotiated and error occurred during
    // during call; schedule a reconnect
    if (this._shouldReconnect()) {
      // this.broker.started => whether the reconnect should happen immediately.
      // If this session previously established connection (N-sessions back)
      // and it failed abruptly, then the timeout is overridden to a intermediate value
      // (BASE_RECONNECTION_TIMEOUT)
      let overrideTimeout;
      if (this.broker?.started) {
        overrideTimeout = 0;
      } else if (this.startedOnce) {
        overrideTimeout = BridgeService.BASE_RECONNECTION_TIMEOUT();
      }

      this.scheduleReconnect({ overrideTimeout });
    }

    return error;
  }

  async view(streamId, options = {
    hasAudio: false,
    outputDeviceId: null,
  }) {
    logger.info({
      logCode: 'screenshare_viewer_bridge_view_start',
      extraInfo: {
        streamId,
        hasAudio: options.hasAudio,
        outputDeviceId: options.outputDeviceId,
      },
    }, `[SCREENSHARE VIEWER] Bridge view() called - streamId=${streamId}`);

    const SETTINGS = window.meetingClientSettings;
    const SFU_CONFIG = SETTINGS.public.kurento;
    const SFU_URL = SFU_CONFIG.wsUrl;
    const OFFERING = SFU_CONFIG.screenshare.subscriberOffering;
    const SIGNAL_CANDIDATES = SFU_CONFIG.signalCandidates;
    const TRACE_LOGS = SFU_CONFIG.traceLogs;
    const GATHERING_TIMEOUT = SFU_CONFIG.gatheringTimeout;
    this.streamId = streamId;
    this.hasAudio = options.hasAudio;
    this.outputDeviceId = options.outputDeviceId;
    this.role = RECV_ROLE;
    // Reset reconnect cycle count on fresh view() — prevents stale counter
    // from a previous mount/unmount cycle poisoning the retry budget
    this._reconnectCycleCount = 0;

    logger.info({
      logCode: 'screenshare_viewer_bridge_config',
      extraInfo: {
        sfuUrl: SFU_URL,
        offering: OFFERING,
        signalCandidates: SIGNAL_CANDIDATES,
        gatheringTimeout: GATHERING_TIMEOUT,
      },
    }, `[SCREENSHARE VIEWER] SFU config - wsUrl=${SFU_URL}`);

    const iceServers = await BridgeService.getIceServers(Auth.sessionToken);

    logger.info({
      logCode: 'screenshare_viewer_ice_servers',
      extraInfo: {
        iceServerCount: iceServers?.length || 0,
      },
    }, `[SCREENSHARE VIEWER] Got ${iceServers?.length || 0} ICE servers`);

    const brokerOptions = {
      iceServers,
      userName: Auth.fullname,
      hasAudio: options.hasAudio,
      offering: OFFERING,
      mediaServer: BridgeService.getMediaServerAdapter(),
      signalCandidates: SIGNAL_CANDIDATES,
      forceRelay: shouldForceRelay() || this._forceRelay,
      traceLogs: TRACE_LOGS,
      gatheringTimeout: GATHERING_TIMEOUT,
      // ICE restart only works for publishers right now - recvonly full
      // reconnection works ok without it.
      restartIce: false,
    };

    this.broker = new ScreenshareBroker(
      Auth.authenticateURL(SFU_URL),
      BridgeService.getConferenceBridge(),
      Auth.userID,
      Auth.meetingID,
      this.role,
      brokerOptions,
    );

    logger.info({
      logCode: 'screenshare_viewer_broker_created',
      extraInfo: {
        role: this.role,
        meetingID: Auth.meetingID,
        userID: Auth.userID,
      },
    }, `[SCREENSHARE VIEWER] ScreenshareBroker created - role=${this.role}`);

    this.broker.onstart = this.handleViewerStart.bind(this);
    this.broker.onerror = this.handleBrokerFailure.bind(this);
    if (!this.reconnecting) {
      this.broker.onended = this.handleEnded.bind(this);
    }
    return this.broker.view().finally(this.scheduleReconnect.bind(this));
  }

  handlePresenterStart() {
    logger.info({
      logCode: 'screenshare_presenter_start_success',
    }, 'Screenshare presenter started succesfully');
    this.clearReconnectionTimeout();
    this.startedOnce = true;
    this.reconnecting = false;
    this.connectionAttempts = 0;
    this._reconnectCycleCount = 0;
    this._forceRelay = false;
  }

  handleEnded() {
    screenShareEndAlert();
  }

  // [Fix #10] Refactored from Promise constructor anti-pattern (async inside
  // new Promise) to plain async method. Previously, unexpected throws before
  // explicit reject() calls were silently swallowed.
  async share(stream, onFailure, contentType) {
    logger.info({
      logCode: 'screenshare_kurento_share_called',
      extraInfo: {
        contentType,
        hasStream: !!stream,
        streamActive: stream?.active,
        trackCount: stream?.getTracks()?.length,
        connectionAttempts: this.connectionAttempts,
      },
    }, `[SCREENSHARE DEBUG] KurentoBridge.share() called — contentType=${contentType}, streamActive=${stream?.active}`);
    const SETTINGS = window.meetingClientSettings;
    const SFU_CONFIG = SETTINGS.public.kurento;
    const SFU_URL = SFU_CONFIG.wsUrl;
    const SIGNAL_CANDIDATES = SFU_CONFIG.signalCandidates;
    const TRACE_LOGS = SFU_CONFIG.traceLogs;
    const { screenshare: NETWORK_PRIORITY } = SETTINGS.public.media.networkPriorities || {};
    const GATHERING_TIMEOUT = SFU_CONFIG.gatheringTimeout;
    const {
      enabled: RESTART_ICE = false,
      retries: RESTART_ICE_RETRIES = 3,
    } = SFU_CONFIG.restartIce?.screenshare || {};
    this.onerror = onFailure;
    this.connectionAttempts += 1;
    this.role = SEND_ROLE;
    this.hasAudio = BridgeService.streamHasAudioTrack(stream);
    this.gdmStream = stream;

    const onerror = (error) => {
      const normalizedError = this.handleBrokerFailure(error);
      if (!this.broker.started) {
      // Broker hasn't started - if there are retries left, try again.
        if (this.maxConnectionAttemptsReached()) {
          this.clearReconnectionTimeout();
          this.connectionAttempts = 0;
          onFailure(SCREENSHARING_ERRORS.MEDIA_TIMEOUT);
        }
      } else if (!this._shouldReconnect()) {
        // Broker has started - should reconnect? If it shouldn't, end it.
        onFailure(normalizedError);
      }
    };

    const iceServers = await BridgeService.getIceServers(Auth.sessionToken);
    const options = {
      iceServers,
      userName: Auth.fullname,
      stream,
      hasAudio: this.hasAudio,
      contentType: contentType,
      bitrate: BridgeService.BASE_BITRATE(),
      offering: true,
      mediaServer: BridgeService.getMediaServerAdapter(),
      signalCandidates: SIGNAL_CANDIDATES,
      forceRelay: shouldForceRelay() || this._forceRelay,
      traceLogs: TRACE_LOGS,
      networkPriority: NETWORK_PRIORITY,
      gatheringTimeout: GATHERING_TIMEOUT,
      restartIce: RESTART_ICE,
      restartIceMaxRetries: RESTART_ICE_RETRIES,
    };

    this.broker = new ScreenshareBroker(
      Auth.authenticateURL(SFU_URL),
      BridgeService.getConferenceBridge(),
      Auth.userID,
      Auth.meetingID,
      this.role,
      options,
    );

    this.broker.onerror = onerror.bind(this);
    this.broker.onstreamended = this.stop.bind(this);
    this.broker.onstart = this.handlePresenterStart.bind(this);
    this.broker.onended = this.handleEnded.bind(this);

    try {
      logger.info({
        logCode: 'screenshare_kurento_broker_share_start',
        extraInfo: { role: this.role, meetingID: Auth.meetingID, userID: Auth.userID },
      }, `[SCREENSHARE DEBUG] Calling broker.share() — SFU negotiation starting`);
      await this.broker.share();
      logger.info({ logCode: 'screenshare_kurento_broker_share_done' }, '[SCREENSHARE DEBUG] broker.share() resolved — scheduling reconnect');
      this.scheduleReconnect();
    } catch (error) {
      logger.error({
        logCode: 'screenshare_kurento_broker_share_error',
        extraInfo: { errorCode: error?.errorCode, errorMessage: error?.errorMessage },
      }, `[SCREENSHARE DEBUG] broker.share() threw: ${error?.errorMessage}`);
      throw mapErrorCode(error);
    }
  }

  // This is a reconnect-safe internal method. Should be used when one wants
  // to clear the internal components (ie broker, connection timeouts) without
  // affecting externally controlled components (ie gDM stream,
  // media tag, connectionAttempts, ...)
  _stop() {
    logger.info({
      logCode: 'screenshare_kurento_internal_stop',
      extraInfo: {
        hasBroker: !!this.broker,
        role: this.role,
        reconnecting: this.reconnecting,
        brokerRole: this.broker?.role,
        brokerStarted: this.broker?.started,
      },
    }, `[SCREENSHARE DEBUG] KurentoBridge._stop() — role=${this.role}, reconnecting=${this.reconnecting}`);

    this._stopQualityMonitor();

    if (this.broker) {
      this.broker.stop();
      // Checks if this session is a sharer and if it's not reconnecting
      // If that's the case, clear the local sharing state in screen sharing UI
      // component tracker to be extra sure we won't have any client-side state
      // inconsistency - prlanzarin
      if (this.broker && this.broker.role === SEND_ROLE && !this.reconnecting) {
        setIsSharing(false);
      }
      this.broker = null;
    }

    this.clearReconnectionTimeout();
  }

  stop() {
    logger.info({
      logCode: 'screenshare_kurento_stop',
      extraInfo: {
        role: this.role,
        hasGdmStream: !!this.gdmStream,
        gdmStreamActive: this.gdmStream?.active,
        connectionAttempts: this.connectionAttempts,
      },
    }, `[SCREENSHARE DEBUG] KurentoBridge.stop() — role=${this.role}, gdmActive=${this.gdmStream?.active}`);
    const mediaElement = document.getElementById(SCREENSHARE_VIDEO_TAG);

    this._stop();
    this.connectionAttempts = 0;

    if (mediaElement && typeof mediaElement.pause === 'function') {
      mediaElement.pause();
      mediaElement.srcObject = null;
    }

    if (this.gdmStream) {
      MediaStreamUtils.stopMediaStreamTracks(this.gdmStream);
      this.gdmStream = null;
    }

    this.outputDeviceId = null;
  }
}
